import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@workspace/ui/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,background-color,border-color,box-shadow] overflow-hidden ring-1 ring-white/10 dark:ring-white/5 shadow-xs",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary/90 text-primary-foreground [a&]:hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary/90 text-secondary-foreground [a&]:hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive/90 text-destructive-foreground [a&]:hover:bg-destructive/80 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border-border bg-background/50 text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground dark:bg-background/30",
        success:
          "border-transparent bg-success/90 text-success-foreground [a&]:hover:bg-success/80",
        warning:
          "border-transparent bg-warning/90 text-warning-foreground [a&]:hover:bg-warning/80",
        accent:
          "border-transparent bg-accent/90 text-accent-foreground [a&]:hover:bg-accent/80",
        destructiveOutline:
          "border-destructive/40 bg-destructive/5 text-destructive [a&]:hover:bg-destructive/10",
        warningOutline:
          "border-warning/40 bg-warning/5 text-warning [a&]:hover:bg-warning/10",
        successOutline:
          "border-success/40 bg-success/5 text-success [a&]:hover:bg-success/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
